import React from 'react';
import styles from './style.module.scss';
import { searchFilterIds } from 'constants/test-constants';
import type { PropertyType, PropertyTypeFilter } from 'contexts/preferences/listing-params/types';
import { trackEvent } from 'utils/google-tag-manager';
import { MORE_FILTERS_OPTIONS_TO_GTM_EVENT } from 'constants/area-map-page-filters';
import Toggle from 'components/control/toggle';
import { buildClassName } from 'utils/build-class-name';
import { PropertyTypeOption, legacyPropertyTypeOptions } from 'utils/select-options';
import { useTranslation } from 'next-i18next';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';
import {
  CommercialIcon,
  CondoIcon,
  FarmIcon,
  HouseIcon,
  LandIcon,
  TownhouseIcon,
} from 'components/icon/filters';

//#region Types
interface FilterDropdownPropertyTypeProps {
  propertyTypeList?: PropertyTypeOption[];
  checkboxNamePrefix?: string;
  currentHomeTypeFilter: PropertyTypeFilter;
  isInDropdown?: boolean;
  onValueChange: (homeType: PropertyTypeFilter) => void;
}
//#endregion

export default function FilterDropdownPropertyType({ checkboxNamePrefix = '', currentHomeTypeFilter, onValueChange, isInDropdown = true, propertyTypeList = legacyPropertyTypeOptions }: FilterDropdownPropertyTypeProps) {
  const { t } = useTranslation(Namespaces.SEARCH);

  const handleClick = (value: PropertyType) => {
    const newValue = !currentHomeTypeFilter[value];

    onValueChange({
      ...currentHomeTypeFilter,
      [value]: newValue,
    });

    if (newValue && !isInDropdown) {
      trackEvent(MORE_FILTERS_OPTIONS_TO_GTM_EVENT[value]);
    }
  };

  const isActive = (value: PropertyType) => {
    return currentHomeTypeFilter[value];
  };

  const getLocalizedLabel = (propertyType: PropertyType) => {
    const localeKeyMap: Record<PropertyType, string> = {
      house: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_HOUSE,
      townhouse: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_TOWNHOUSE,
      condo: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_CONDO,
      land: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_LAND,
      commercial: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_COMMERCIAL,
      farm: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_FARM,
    };
    return t(localeKeyMap[propertyType], { defaultValue: propertyType });
  };

  const mapIcon = (propertyType: PropertyType) => {
    switch (propertyType) {
      case 'house':
        return HouseIcon;
      case 'townhouse':
        return TownhouseIcon;
      case 'condo':
        return CondoIcon;
      case 'land':
        return LandIcon;
      case 'commercial':
        return CommercialIcon;
      case 'farm':
        return FarmIcon;
      default:
        return null; 
    }
  };


  const isHouse = (filter: PropertyTypeFilter) => filter.house === true;

  return (
    <div className={buildClassName(styles.component, !isInDropdown && styles['no-padding'])}>
      {propertyTypeList.map((item, index) => {
        let indentedOptions;
        if (item.options) {
          indentedOptions = (
            <div key={index} className={`${styles.indented} ${isHouse(currentHomeTypeFilter) ? styles.shown : styles.hidden}`}>
              {item.options.map(({ label, value }) => {
                const localizedLabel = getLocalizedLabel(value);
                return (
                  <Toggle
                    key={label}
                    label={localizedLabel}
                    name={checkboxNamePrefix + localizedLabel}
                    value={isActive(value)}
                    onClick={() => handleClick(value)}
                    testId={localizedLabel + 'CheckboxOption'}
                    isFullWidth
                    Icon={mapIcon(value)}
                  />
                );
              })}
            </div>
          );
        }
        return (
          <div key={index}>
            <Toggle
              label={getLocalizedLabel(item.value)}
              name={checkboxNamePrefix + getLocalizedLabel(item.value)}
              value={isActive(item.value)}
              onClick={() => handleClick(item.value)}
              testId={getLocalizedLabel(item.value) + searchFilterIds.checkboxOption}
              isFullWidth
              Icon={mapIcon(item.value)}
            />
            {indentedOptions}
          </div>
        );
      })}
    </div>
  );
}
